import { CardConfig } from 'components/DynamicDashboard/interfaces';
import i18n from 'i18n';
import {
  getBlockMultipleSessionReportRequest,
  getClientVirtualizerReportRequest,
  getConnectionTypeReportRequest,
  getMFAReportRequest,
  getUseProfileReportRequest,
} from 'modules/clients/actions';
import { useDispatch, useSelector } from 'react-redux';
import ApplicationState from 'store/interfaces';
import palette from 'theme/palette';

const DEFAULT_SELECTED_CARDS = [
  'connection_type',
  'user_mfa_is_active',
  'virtualizer',
];

const useClientsDashboardSelector = ({
  clients: {
    blockMultipleSessionReport,
    isLoadingBlockMultipleSessionReport,
    connectionTypeReport,
    isLoadingConnectionTypeReport,
    isLoading,
    MFAReport,
    isLoadingMFAReport,
    useProfileReport,
    isLoadingUseProfileReport,
    virtualizerReport,
    isLoadingVirtualizerReport,
  },
}: ApplicationState) => ({
  blockMultipleSessionReport,
  connectionTypeReport,
  isLoading:
    isLoading ||
    isLoadingBlockMultipleSessionReport ||
    isLoadingConnectionTypeReport ||
    isLoadingMFAReport ||
    isLoadingUseProfileReport ||
    isLoadingVirtualizerReport,
  MFAReport,
  useProfileReport,
  virtualizerReport,
});

interface Props {
  filters: Record<string, string>;
}

export const useClientsDashboard = ({ filters }: Props) => {
  const {
    blockMultipleSessionReport,
    connectionTypeReport,
    isLoading,
    MFAReport,
    useProfileReport,
    virtualizerReport,
  } = useSelector(useClientsDashboardSelector);
  const dispatch = useDispatch();

  const cardsConfig: CardConfig[] = [
    {
      key: 'block_multiple_session',
      title: 'DYNAMIC_FILTER.BLOCK_MULTIPLE_SESSION_KEY',
      modalTitle: 'CLIENTS_BLOCK_MULTIPLE_SESSION_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_CLIENTS_STATUS_KEY',
      report: blockMultipleSessionReport,
      getReport: () =>
        dispatch(getBlockMultipleSessionReportRequest({ filters })),
      options: [
        {
          field: 'activate',
          value: 'true',
          label: 'ACTIVE_KEY',
          color: palette.digitalBlue,
        },
        {
          field: 'deactivate',
          value: 'false',
          label: 'INACTIVE_KEY',
          color: palette.deepBlue,
        },
      ],
    },
    {
      key: 'connection_type',
      title: 'CONNECTION_TYPE_KEY',
      modalTitle: 'CLIENTS_CONNECTION_TYPE_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_CLIENTS_CONNECTION_TYPE_KEY',
      report: connectionTypeReport,
      getReport: () => dispatch(getConnectionTypeReportRequest({ filters })),
      options: [
        {
          field: 'private',
          value: 'private',
          label: 'BULK_CLIENTS.PRIVATE_KEY',
          color: palette.digitalBlue,
        },
        {
          field: 'public',
          value: 'public',
          label: 'BULK_CLIENTS.PUBLIC_KEY',
          color: palette.deepBlue,
        },
      ],
    },
    {
      key: 'virtualizer',
      title: 'VIRTUALIZER_KEY',
      tooltip: 'CLICK_TO_FILTER_CLIENTS_VIRTUALIZER_KEY',
      modalTitle: 'CLIENTS_VIRTUALIZER_GRAPHS_KEY',
      multiple: true,
      report: virtualizerReport,
      getReport: () => dispatch(getClientVirtualizerReportRequest({ filters })),
      options: [
        {
          field: 'connect',
          value: 'connect',
          color: palette.lightBlue,
          label: 'ConnectSky',
        },
        {
          field: 'connect_direct',
          value: 'connect_direct',
          color: palette.deepBlueLight1,
          label: 'ConnectSky (Direct Connect)',
        },
        {
          field: 'rdp',
          value: 'rdp',
          color: palette.lightBlueLight2,
          label: `Microsoft Remote Desktop (${i18n.t('FILE_KEY')})`,
        },
        {
          field: 'rdp_direct',
          value: 'rdp_direct',
          color: palette.digitalBlueLight3,
          label: 'Microsoft Remote Desktop (Direct Connect)',
        },
        {
          field: 'rdp_app',
          value: 'rdp_app',
          color: palette.digitalBlueDark2,
          label: 'Microsoft Remote Desktop (RemoteApp)',
        },
        {
          field: 'tsplus',
          value: 'tsplus',
          color: palette.digitalBlue,
          label: 'TSPlus (WebBrowser HTML5)',
        },
        {
          field: 'tsplus_remote_app',
          value: 'tsplus_remote_app',
          color: palette.deepBlue,
          label: 'TSPlus (RemoteApp)',
        },
        {
          field: 'tsplus_all',
          value: 'tsplus_all',
          color: palette.lightBlueDark2,
          label: 'TSPlus (WebBrowser HTML and RemoteApp)',
        },
        {
          field: 'aspw',
          value: 'aspw',
          color: palette.deepBlueDark2,
          label: 'ASPW (WebBrowser)',
        },
        {
          field: 'aspw_all',
          value: 'aspw_all',
          color: palette.digitalBlueLight1,
          label: 'ASPW (WebBrowser and RemoteApp)',
        },
      ],
    },
    {
      key: 'use_profile',
      title: 'LOGS_PAGE.FIELDS.USE_PROFILE_KEY',
      modalTitle: 'CLIENTS_USE_PROFILE_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_CLIENTS_STATUS_KEY',
      report: useProfileReport,
      getReport: () => dispatch(getUseProfileReportRequest({ filters })),
      options: [
        {
          field: 'activate',
          value: 'true',
          label: 'ACTIVE_KEY',
          color: palette.digitalBlue,
        },
        {
          field: 'deactivate',
          value: 'false',
          label: 'INACTIVE_KEY',
          color: palette.deepBlue,
        },
      ],
    },
    {
      key: 'user_mfa_is_active',
      title: 'MFA',
      modalTitle: 'CLIENTS_MFA_GRAPHS_KEY',
      tooltip: 'CLICK_TO_FILTER_CLIENTS_STATUS_KEY',
      report: MFAReport,
      getReport: () => dispatch(getMFAReportRequest({ filters })),
      options: [
        {
          field: 'activate',
          value: 'true',
          label: 'ACTIVE_KEY',
          color: palette.digitalBlue,
        },
        {
          field: 'deactivate',
          value: 'false',
          label: 'INACTIVE_KEY',
          color: palette.deepBlue,
        },
      ],
    },
  ];

  return {
    cardsConfig,
    defaultSelectedCards: DEFAULT_SELECTED_CARDS,
    isLoading,
  };
};
